import { FileDown, ShieldCheck } from "lucide-react";
import type { AgentStatus } from "@shared/agent-plan";

function dateLabel(value: string | null) {
  return value ? new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";
}

function money(cents: number) {
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(cents / 100);
}

export default function AgentSmsControls({ status }: { status: AgentStatus }) {
  const used = Math.max(0, status.usage.used);
  const remaining = Math.max(0, status.usage.remaining);
  const usagePct = Math.min(100, (used / Math.max(1, status.usage.included)) * 100);

  return (
    <div className="space-y-4">
      <section className="rounded-2xl border border-emerald-300/20 bg-emerald-300/[.05] p-5" aria-label="Agent reply usage">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-slate-500 text-[10px] uppercase tracking-[.16em] font-bold">Conversational SMS</p>
            <h2 className="text-white text-lg font-bold mt-1">{status.usage.included} outgoing replies each billing month</h2>
          </div>
          <span className="rounded-full border border-emerald-300/20 bg-emerald-300/10 px-2 py-1 text-[10px] font-bold text-emerald-200">{remaining} left</span>
        </div>
        <div className="mt-4 h-2 rounded-full bg-slate-900 overflow-hidden" role="progressbar" aria-label="Agent reply texts used" aria-valuenow={used} aria-valuemin={0} aria-valuemax={status.usage.included}>
          <div className="h-full rounded-full bg-emerald-400 transition-[width] duration-300" style={{ width: `${usagePct}%` }} />
        </div>
        <p className="text-slate-300 text-xs mt-2">{used} of {status.usage.included} outgoing replies used · {status.usage.pendingReplies} pending</p>
        <p className="text-slate-500 text-[11px] mt-1">Billing period {dateLabel(status.usage.periodStart)}–{dateLabel(status.usage.periodEnd)}. Incoming texts do not count. Automated condition alerts use a separate allowance.</p>
        {remaining === 0 && <p className="text-amber-200 text-xs mt-3">Included replies are used for this period. Extra reply collection is currently disabled.</p>}
      </section>

      <section className="rounded-2xl border border-white/10 bg-white/[.04] p-5" aria-label="Agent reply collection status">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-emerald-200" />
          <h2 className="text-white font-semibold text-sm">Reply collection status</h2>
        </div>
        <p className="text-slate-300 text-xs leading-relaxed mt-2">Collection for extra Agent SMS replies is disabled. No payment method setup or paid-reply enrollment is available here.</p>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-xl border border-white/[.08] bg-slate-950/30 p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Historical extra replies</p>
            <p className="text-sm text-slate-100 mt-1">{status.usage.overageReplies.toLocaleString()} replies · {money(status.usage.overageCents)}</p>
          </div>
          <div className="rounded-xl border border-white/[.08] bg-slate-950/30 p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Historical monthly cap</p>
            <p className="text-sm text-slate-100 mt-1">{status.overages.capCents > 0 ? money(status.overages.capCents) : "No cap recorded"}</p>
          </div>
        </div>
        <p className="text-slate-500 text-[11px] mt-3">Previously recorded usage, invoices, consent, and cap details are retained for account history. They do not enable new collection.</p>
      </section>

      <section className="rounded-2xl border border-white/10 bg-white/[.04] p-5">
        <div className="flex items-center justify-between mb-3">
          <div><p className="text-slate-500 text-[10px] uppercase tracking-[.16em] font-bold">Agent billing history</p><h2 className="text-white font-semibold text-sm mt-1">Reply records</h2></div>
          <FileDown size={17} className="text-slate-500" />
        </div>
        {status.invoices.length === 0 ? <p className="text-slate-400 text-xs">No Agent reply records are available.</p> : (
          <div className="divide-y divide-white/[.07]">{status.invoices.map(invoice => <div key={invoice.id} className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
            <div><p className="text-white text-xs font-semibold">{money(invoice.amountCents)}</p><p className="text-slate-500 text-[11px] mt-0.5">{invoice.replies} replies · {dateLabel(invoice.periodStart)}–{dateLabel(invoice.periodEnd)}</p></div>
            <span className="rounded-full border border-white/10 px-2 py-1 text-[10px] text-slate-300">{invoice.status}</span>
          </div>)}</div>
        )}
        <p className="text-slate-500 text-[10px] mt-3">Historical billing records are displayed read-only and do not initiate a payment.</p>
      </section>
    </div>
  );
}
